'use client';

import Script from 'next/script';
import React from 'react';
import {
  useConsentManagement,
  useUserInteraction,
} from '@/hooks/usePerformance';
import { TRACKING_CONFIG, LOADING_DELAYS } from '@/utils/trackingConfig';

/* eslint-disable @typescript-eslint/no-explicit-any, prefer-rest-params, @typescript-eslint/no-unused-expressions */

interface ThirdPartyScriptsProps {
  gtmId?: string;
}

interface GeolocationResponse {
  country: string;
  ip: string | null;
  source: string;
  isGDPR: boolean;
}

/**
 * Optimized third-party scripts component implementing Google Consent Mode v2
 * with region-specific default consent behavior:
 * 1. GDPR regions (EU/EEA): Default to 'denied', require explicit consent
 * 2. US and other regions: Default to 'granted', allow opt-out
 * 3. Always show banner for transparency and user control
 * 4. Update consent state when user accepts/rejects
 */
export const ThirdPartyScripts = ({
  gtmId = TRACKING_CONFIG.gtm.id,
}: ThirdPartyScriptsProps) => {
  const { consentGiven, consentLoaded } = useConsentManagement();
  const userInteracted = useUserInteraction();
  const [isGDPRRegion, setIsGDPRRegion] = React.useState<boolean>(false);
  const [regionLoaded, setRegionLoaded] = React.useState<boolean>(false);

  // Detect user's region using our production geolocation API
  React.useEffect(() => {
    const detectRegion = async () => {
      try {
        const response = await fetch('/api/geolocation');
        if (response.ok) {
          const data: GeolocationResponse = await response.json();
          setIsGDPRRegion(data.isGDPR);
        } else {
          setIsGDPRRegion(false); // Default to non-GDPR
        }
      } catch (error) {
        console.warn('Failed to detect region, defaulting to non-GDPR:', error);
        setIsGDPRRegion(false);
      } finally {
        setRegionLoaded(true);
      }
    };

    detectRegion();
  }, []);

  // Google Tag always loads in production (with consent mode)
  const isProduction = process.env.NODE_ENV === 'production';
  const shouldLoadGoogleTag =
    isProduction && gtmId && TRACKING_CONFIG.gtm.enabled && regionLoaded;

  // Non-critical scripts:
  // - In GDPR: require consent + interaction
  // - Outside GDPR: load as soon as region is known (no gating/delay)
  const shouldLoadNonCriticalScripts =
    isProduction &&
    regionLoaded &&
    (isGDPRRegion ? consentLoaded && consentGiven && userInteracted : true);

  // Helper to optionally delay only in GDPR regions
  const runWithOptionalDelay = (fn: () => void, delay: number) => {
    if (isGDPRRegion) {
      setTimeout(fn, delay);
    } else {
      fn();
    }
  };

  return (
    <>
      {/* Google Consent Mode v2 - Region-specific default consent */}
      {shouldLoadGoogleTag && (
        <Script
          id="gtag-consent-default"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              
              // Set region-specific default consent
              ${
                isGDPRRegion
                  ? `
              // GDPR region: Default to 'denied' - require explicit consent
              gtag('consent', 'default', {
                'ad_storage': 'denied',
                'ad_user_data': 'denied', 
                'ad_personalization': 'denied',
                'analytics_storage': 'denied',
                'region': ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB'],
                'wait_for_update': 500
              });
              `
                  : `
              // Non-GDPR region: Default to 'granted' - allow opt-out
              gtag('consent', 'default', {
                'ad_storage': 'granted',
                'ad_user_data': 'granted',
                'ad_personalization': 'granted', 
                'analytics_storage': 'granted'
              });
              `
              }
              
              // Enable URL passthrough for better measurement without cookies
              gtag('set', 'url_passthrough', true);
              gtag('set', 'ads_data_redaction', true);
            `,
          }}
        />
      )}

      {/* Google Tag (gtag.js) - Always loads after consent mode setup */}
      {shouldLoadGoogleTag && (
        <Script
          src={`https://www.googletagmanager.com/gtag/js?id=${gtmId}`}
          strategy="afterInteractive"
          onLoad={() => {
            // Initialize gtag with config
            window.gtag('js', new Date());
            window.gtag('config', gtmId, {
              page_title: document.title,
              page_location: window.location.href,
            });
          }}
        />
      )}

      {/* Consent Update - Updates consent when user gives permission */}
      {shouldLoadGoogleTag && consentLoaded && consentGiven && (
        <Script
          id="gtag-consent-update"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              // Update consent to 'granted' when user accepts
              gtag('consent', 'update', {
                'ad_storage': 'granted',
                'ad_user_data': 'granted',
                'ad_personalization': 'granted', 
                'analytics_storage': 'granted'
              });
            `,
          }}
        />
      )}

      {/* Google Tag Manager - Delay only in GDPR regions */}
      {shouldLoadNonCriticalScripts && (
        <>
          <Script
            id="gtm-script"
            strategy="afterInteractive"
            onLoad={() => {
              runWithOptionalDelay(() => {
                const script = document.createElement('script');
                script.async = true;
                script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
                document.head.appendChild(script);
              }, LOADING_DELAYS.gtm);
            }}
          />

          {/* GTM NoScript fallback */}
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
        </>
      )}

      {/* Microsoft Clarity - Delay only in GDPR regions */}
      {shouldLoadNonCriticalScripts && TRACKING_CONFIG.clarity.enabled && (
        <Script
          id="clarity-script"
          strategy="afterInteractive"
          onLoad={() => {
            runWithOptionalDelay(() => {
              (function (c: any, l: Document, a: string, r: string, i: string) {
                c[a] =
                  c[a] ||
                  function () {
                    (c[a].q = c[a].q || []).push(arguments);
                  };
                const t = l.createElement(r) as HTMLScriptElement;
                t.async = true;
                t.src = 'https://www.clarity.ms/tag/' + i;
                const y = l.getElementsByTagName(r)[0];
                y.parentNode?.insertBefore(t, y);
              })(
                window,
                document,
                'clarity',
                'script',
                TRACKING_CONFIG.clarity.id,
              );
            }, LOADING_DELAYS.clarity);
          }}
        />
      )}

      {/* LinkedIn Insight Tag - Delay only in GDPR regions */}
      {shouldLoadNonCriticalScripts && TRACKING_CONFIG.linkedin.enabled && (
        <Script
          id="linkedin-insight"
          strategy="afterInteractive"
          onLoad={() => {
            runWithOptionalDelay(() => {
              window._linkedin_partner_id = TRACKING_CONFIG.linkedin.partnerId;
              window._linkedin_data_partner_ids =
                window._linkedin_data_partner_ids || [];
              window._linkedin_data_partner_ids.push(
                window._linkedin_partner_id,
              );

              const script = document.createElement('script');
              script.type = 'text/javascript';
              script.async = true;
              script.src =
                'https://snap.licdn.com/li.lms-analytics/insight.min.js';
              document.head.appendChild(script);
            }, LOADING_DELAYS.linkedin);
          }}
        />
      )}

      {/* Facebook Pixel - Delay only in GDPR regions */}
      {shouldLoadNonCriticalScripts && TRACKING_CONFIG.facebook.enabled && (
        <Script
          id="facebook-pixel"
          strategy="afterInteractive"
          onLoad={() => {
            runWithOptionalDelay(() => {
              (function (
                f: any,
                b: Document,
                e: string,
                v: string,
                n?: any,
                t?: any,
                s?: any,
              ) {
                if (f.fbq) return;
                n = f.fbq = function () {
                  n.callMethod
                    ? n.callMethod.apply(n, arguments)
                    : n.queue.push(arguments);
                };
                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = true;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = true;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                if (s?.parentNode) {
                  s.parentNode.insertBefore(t, s);
                }
              })(
                window,
                document,
                'script',
                'https://connect.facebook.net/en_US/fbevents.js',
              );

              window.fbq('init', TRACKING_CONFIG.facebook.pixelId);
              window.fbq('track', 'PageView');
            }, LOADING_DELAYS.facebook);
          }}
        />
      )}

      {/* Albacross - Delay only in GDPR regions */}
      {shouldLoadNonCriticalScripts && TRACKING_CONFIG.albacross.enabled && (
        <Script
          id="albacross-script"
          strategy="afterInteractive"
          onLoad={() => {
            runWithOptionalDelay(() => {
              const script = document.createElement('script');
              script.async = true;
              script.src = TRACKING_CONFIG.albacross.scriptUrl;
              document.head.appendChild(script);
            }, LOADING_DELAYS.albacross);
          }}
        />
      )}

      {/* Apollo - Delay only in GDPR regions */}
      {shouldLoadNonCriticalScripts && TRACKING_CONFIG.apollo.enabled && (
        <Script
          id="apollo-script"
          strategy="afterInteractive"
          onLoad={() => {
            runWithOptionalDelay(() => {
              const script = document.createElement('script');
              script.src = TRACKING_CONFIG.apollo.scriptUrl;
              script.async = true;
              document.head.appendChild(script);
            }, LOADING_DELAYS.apollo);
          }}
        />
      )}
    </>
  );
};

// Default export for backward compatibility
export default ThirdPartyScripts;
